import React, { useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { ROOMS } from '../rooms';
import { useWorldStore } from '../state/worldStore';
import { useCounterItems, clearCounterItems } from '../state/counterItems';

// CounterItems — what a job rang up, set on its room's counter.
//
// Khari, 2026-10-30: "another type of job: grocer/cashier. We have a corner
// market with a job for an artist to work the cash register."
//
// The cashier's ring_up (API GroceryService) comes back as `purchase: { items }`
// and worldJob applyRoleOut puts it in state/counterItems, for any room with a
// `counter` spot (rooms.js). This draws it: after a beat of scanning, a paper
// bag on the counter with the tops of a few things showing, and anything past
// three set down beside it. Gone after a minute (the store's timer) or the moment
// you leave the room (unmount, below) — a bag in a shop you walked out of was
// simply taken.
//
// AND THE HANDS: while something is being rung up, whoever is cast in the job is
// sent to the room's `register` mark (`stage['*'].register`, the same job-keyed
// staging the café's barista walks by), and back home once the bag is gone. A
// body placed at the register just squares up to the customer.
//
// Room space, like the café's cup: inside the room group, outside the building's
// scale group (MeshInterior), so the bag is sized against a PERSON (a resident is
// ~0.75-0.85u tall; 1u ≈ 2.2m), not against the building's fit. Cheap on
// purpose: one to six untextured meshes.
//
// THE SHAPES DO NOT GUESS FROM THE WORDS. "oat milk" does not become a carton:
// a word list would be wrong as often as right and would grow forever. Each name
// picks a shape and a colour from a stable hash, so the same basket always looks
// the same and nothing claims to be something it is not. The names themselves
// are in the counter dialog.

const BAG = { w: 0.077, h: 0.164, d: 0.127 };   // a kraft grocery bag: ~17 x 36 x 28 cm
const KRAFT = '#b98b55';
const INSIDE = '#4a331d';

const TOPS = [
  // poking out of the bag: [kind, height, palette]
  ['bottle', 0.12, ['#3f8f5a', '#c9452f', '#2f6fb3', '#e8e2d0']],
  ['box', 0.085, ['#e3b23c', '#d9534f', '#4a7fc1', '#f2efe6', '#7a4fa0']],
  ['stalk', 0.14, ['#5d9b3a', '#c99a5b', '#7fb04a']],
];
const LOOSE = [
  // set down beside it
  ['can', 0.055, ['#c0392b', '#2c7be5', '#d4d4d4', '#f1c40f']],
  ['fruit', 0.034, ['#f39c12', '#c0392b', '#9acd32', '#f4d03f']],
];

function hash(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

function Piece({ kind, h, color, at, tilt = 0 }) {
  const mat = <meshStandardMaterial color={color} roughness={kind === 'bottle' || kind === 'can' ? 0.35 : 0.7} metalness={kind === 'can' ? 0.5 : 0} />;
  if (kind === 'bottle') return <mesh position={at} rotation={[0, 0, tilt]}><cylinderGeometry args={[0.008, 0.015, h, 14]} />{mat}</mesh>;
  if (kind === 'box') return <mesh position={at} rotation={[0, tilt * 3, tilt]}><boxGeometry args={[0.03, h, 0.045]} />{mat}</mesh>;
  if (kind === 'stalk') return <mesh position={at} rotation={[tilt, 0, tilt * 1.5]}><cylinderGeometry args={[0.011, 0.009, h, 10]} />{mat}</mesh>;
  if (kind === 'can') return <mesh position={at}><cylinderGeometry args={[0.015, 0.015, h, 16]} />{mat}</mesh>;
  return <mesh position={at}><sphereGeometry args={[h / 2, 14, 10]} />{mat}</mesh>;
}

function Bag({ items }) {
  // Up to three tops in the bag, spread along its long side (z); the next two
  // beside it, further down the counter. Bases sit inside the bag so each top
  // shows ~3-5 cm above the rim.
  const tops = items.slice(0, 3).map((name, i, all) => {
    const k = hash(name);
    const [kind, h, palette] = TOPS[k % TOPS.length];
    const z = all.length === 1 ? 0 : (i - (all.length - 1) / 2) * 0.034;
    const poke = 0.03 + ((k >> 4) % 3) * 0.01;
    return { name, kind, h, color: palette[(k >> 8) % palette.length], at: [((k >> 12) % 3 - 1) * 0.012, BAG.h + poke - h / 2, z], tilt: (((k >> 16) % 5) - 2) * 0.06 };
  });
  const loose = items.slice(3, 5).map((name, i) => {
    const k = hash(name);
    const [kind, h, palette] = LOOSE[k % LOOSE.length];
    return { name, kind, h, color: palette[(k >> 8) % palette.length], at: [((k >> 12) % 3 - 1) * 0.015, h / 2, BAG.d / 2 + 0.03 + i * 0.045] };
  });
  return (
    <>
      <mesh position={[0, BAG.h / 2, 0]}>
        <boxGeometry args={[BAG.w, BAG.h, BAG.d]} />
        <meshStandardMaterial color={KRAFT} roughness={0.95} metalness={0} />
      </mesh>
      {/* The open top, so it reads as a bag rather than a block. */}
      <mesh position={[0, BAG.h - 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[BAG.w * 0.92, BAG.d * 0.94]} />
        <meshStandardMaterial color={INSIDE} roughness={1} metalness={0} />
      </mesh>
      {tops.map((p) => <Piece key={`t-${p.name}`} {...p} />)}
      {loose.map((p) => <Piece key={`l-${p.name}`} {...p} />)}
    </>
  );
}

export default function CounterItems({ roomId }) {
  const room = ROOMS[roomId];
  const at = room?.counter || null;
  const current = useCounterItems((s) => (s.current?.zone === roomId ? s.current : null));
  const group = useRef();
  const staged = useRef(null);

  // THE HANDS: to the register while it is rung up and bagged, home after.
  const purchaseId = current?.id || null;
  const actorKey = current?.roleKey ? `actor:${current.roleKey}` : null;
  useEffect(() => {
    const ws = useWorldStore.getState();
    if (purchaseId && actorKey && room?.stage?.['*']?.register) {
      ws.stageTo(actorKey, 'register');
      staged.current = actorKey;
    } else if (!purchaseId && staged.current) {
      ws.stageTo(staged.current, null);
      staged.current = null;
    }
  }, [purchaseId, actorKey, room]);

  // Out of the room: the bag goes with you, and the job's body is released.
  useEffect(() => () => {
    clearCounterItems(roomId);
    if (staged.current) useWorldStore.getState().stageTo(staged.current, null);
    staged.current = null;
  }, [roomId]);

  // Set down, not popped in: a short ease up from nothing when it lands.
  const landed = useRef(0);
  useFrame((_, dt) => {
    const g = group.current;
    if (!g) return;
    landed.current = Math.min(1, landed.current + dt * 5);
    const k = 1 - (1 - landed.current) ** 3;
    g.scale.setScalar(0.6 + 0.4 * k);
  });
  const bagged = current?.phase === 'bagged';
  useEffect(() => { if (bagged) landed.current = 0; }, [bagged, purchaseId]);

  if (!at || !bagged) return null;
  return (
    <group ref={group} position={at} scale={0.6}>
      <Bag items={current.items} />
    </group>
  );
}
